import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable, inject } from '@angular/core';
import { GetHolds, PostHold, RemoveHold, SetHold } from './holds.actions';
import { Observable, tap } from 'rxjs';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { Hold } from './models/hold';
import { HoldsService } from './holds.service';
import { LoadingModel } from '../../../_core/state/loading.model';

export type HoldsStateModel = LoadingModel & {
  holds: Hold[]|null;
};

export const initialState: HoldsStateModel = {
  holds: [],
  isLoading: false,
};

@State<HoldsStateModel>({
  name: 'holds',
  defaults: initialState,
})
@Injectable()
export class HoldsState {
  private readonly holdsService = inject(HoldsService);

  @Selector()
  static getHolds(state: HoldsStateModel) {
    return state.holds;
  }

  @Selector()
  static getIsLoading(state: HoldsStateModel) {
    return state.isLoading;
  }

  @Action(GetHolds)
  getHolds(context: StateContext<HoldsStateModel>): Observable<Hold[]> {
    context.setState(patch<HoldsStateModel>({ isLoading: true }));
    return this.holdsService.getHolds().pipe(
      tap((holds) => {
        context.setState(patch<HoldsStateModel>({ holds: holds, isLoading: false }));
      })
    );
  }

  @Action(PostHold)
  postHold(context: StateContext<HoldsStateModel>, { recordId, from, issueDate, rhId }: PostHold) {
    return this.holdsService.postHold(recordId, from, issueDate, rhId).pipe(
      tap((hold) => {
        context.setState(
          patch<HoldsStateModel>({
            holds: insertItem<Hold>(hold, 0),
          })
        );
      })
    );
  }

  @Action(SetHold)
  setHold(context: StateContext<HoldsStateModel>, { updatedHold }: SetHold) {
    context.setState(
      patch({
        holds: updateItem<Hold>((hold) => hold.id === updatedHold.id, updatedHold),
      })
    );
  }

  @Action(RemoveHold)
  deleteHold(context: StateContext<HoldsStateModel>, { holdId }: RemoveHold) {
    return this.holdsService.deleteHold(holdId).pipe(
      tap(() => {
        context.setState(
          patch<HoldsStateModel>({
            holds: removeItem<Hold>((hold) => hold.id === holdId),
          })
        );
      })
    );
  }
}
